import React, {useMemo, useState} from 'react';
import {noop} from 'lodash';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {Button, Icon, TableBar} from '@forecast-it/design-system';
import {ClientContactNode, PersonProjectAccess, ViewerProjectNode} from './CompanyViewPage';
import DropdownMenu from '../shared/components/dropdown-menu/DropdownMenu';
import Util from '../shared/util/util';
import DeleteProjectPersonMutation from '../../mutations/delete_project_person_mutation';
import ClientContactModalContainer from '../admin-tab/clients-page/ClientContactModalContainer';
import EditClientContactModalContainer from '../admin-tab/clients-page/EditClientContactModalContainer';

// ─── Styled components ───────────────────────────────────────────────────────

const PanelWrapper = Styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0 32px;
`;

const ToolbarWrapper = Styled.div`
  padding: 0 24px 0 32px;
`;

const SegmentedControlWrapper = Styled.div`
  display: inline-flex;
  border: 1px solid #6b6d7b;
  border-radius: 8px;
  background: #f5f3f9;
  padding: 4px;
`;

const SegmentButton = Styled.button<{$active: boolean}>`
  width: 80px;
  padding: 4px 10px;
  border-radius: 4px;
  border: ${({$active}) => ($active ? '1px solid #6b6d7b' : 'none')};
  background: ${({$active}) => ($active ? '#ffffff' : 'transparent')};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #232334;
  cursor: pointer;
  text-align: center;
`;

const ContentArea = Styled.div`
  padding: 16px 32px 0;
  flex: 1;
`;

const EmptyStateBox = Styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #d3d3df;
  border-radius: 8px;
  padding: 64px 0;
  background: #ffffff;
`;

const EmptyStateText = Styled.p`
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #000000;
  text-align: center;
  margin: 0;
`;

const CreateContactLink = Styled.button`
  background: none;
  border: none;
  padding: 10px 12px;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #0004db;
  cursor: pointer;
  font-weight: 500;
`;

const ContactTable = Styled.div`
  width: 100%;
`;

const TableRow = Styled.div`
  display: flex;
  align-items: center;
  height: 60px;
  border-bottom: 1px solid #d3d3df;
  padding: 0 8px;
`;

const NameCell = Styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding-left: 24px;
`;

const Avatar = Styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 4px;
  background-color: #0004db;
  font-size: 14px;
  line-height: 20px;
  font-weight: 350;
  letter-spacing: 0.15px;
  text-transform: uppercase;
  color: #ffffff;
`;

const ContactName = Styled.span`
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.15px;
  color: #232334;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TextCell = Styled.div`
  flex: 1;
  min-width: 0;
  padding: 0 8px;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.15px;
  color: #232334;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GuestCell = Styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.15px;
  color: #232334;
  overflow: visible;
`;

const ProjectBadge = Styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  border: 1px solid #6b6d7b;
  background: #ffffff;
  font-size: 10px;
  line-height: 14px;
  letter-spacing: 0.3px;
  color: #232334;
  cursor: pointer;
  position: relative;
`;

const ProjectTooltip = Styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  left: auto;
  z-index: 100;
  background: #ffffff;
  border: 1px solid #d3d3df;
  border-radius: 8px;
  padding: 20px 24px 24px;
  width: 480px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
`;

const TooltipHeader = Styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TooltipTitle = Styled.span`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #232334;
  white-space: nowrap;
`;

const TooltipCloseButton = Styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #232334;
  font-size: 16px;
`;

const TooltipTable = Styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
`;

const TooltipTableRow = Styled.div<{$last?: boolean}>`
  display: contents;
  & > * {
    border-bottom: ${({$last}) => ($last ? 'none' : '1px solid #d3d3df')};
  }
`;

const TooltipHeaderRow = Styled.div`
  display: contents;
  & > * {
    border-bottom: 1px solid #d3d3df;
  }
`;

const TooltipColId = Styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.15px;
  color: #232334;
`;

const TooltipColTitle = Styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.15px;
  color: #232334;
`;

const TooltipColDate = Styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.15px;
  color: #232334;
`;

const TooltipColAction = Styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
`;

const TooltipDataColId = Styled(TooltipColId)`
  font-weight: 350;
  padding: 16px 8px;
`;

const TooltipDataColTitle = Styled(TooltipColTitle)`
  font-weight: 350;
  padding: 16px 8px;
  overflow: hidden;
`;

const TooltipDataColDate = Styled(TooltipColDate)`
  font-weight: 350;
  padding: 16px 8px;
`;

const PROJECT_ID_COLORS = [
	'#ff8fd0',
	'#27bb8f',
	'#4a90d9',
	'#f5a623',
	'#7b68ee',
	'#50c878',
	'#ff6b6b',
	'#ffd700',
	'#20b2aa',
	'#ff7f50',
];

const ProjectIdBadge = Styled.span<{$color: string}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: ${({$color}) => $color};
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.15px;
  color: #232334;
  white-space: nowrap;
`;

const TooltipRemoveButton = Styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  color: #6b6d7b;
  &:hover {
    color: #d03030;
  }
`;

const PrimaryBadgeWrapper = Styled.span`
  position: relative;
  display: inline-flex;
`;

const PrimaryBadge = Styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: #e5f4ff;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.15px;
  color: #232334;
  white-space: nowrap;
  cursor: default;
`;

const PrimaryTooltip = Styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  z-index: 100;
  background: #ffffff;
  border: 1px solid #d3d3df;
  border-radius: 8px;
  padding: 12px 16px;
  width: 280px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
`;

const PrimaryTooltipTitle = Styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #232334;
  margin-bottom: 8px;
`;

const PrimaryTooltipBody = Styled.div`
  font-size: 14px;
  line-height: 20px;
  color: #232334;
`;

const ActionsCell = Styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 160px;
  flex: none;
  padding: 0 8px;
`;

const KebabButton = Styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: #232334;
  border-radius: 8px;
  &:hover {
    background: #f5f3f9;
  }
`;

const ColHeader = Styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.15px;
  color: #232334;
  white-space: nowrap;
`;

const NameColHeader = Styled(ColHeader)`
  flex: 1;
  min-width: 0;
  padding-left: 24px;
`;

const TableHeaderRow = Styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #d3d3df;
  padding: 20px 8px;
`;

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
	clientContacts: ClientContactNode[];
	primaryContactId: string | null;
	viewerProjects: ViewerProjectNode[];
	clientId: string;
	clientName: string;
};

// ─── Component ───────────────────────────────────────────────────────────────

const ContactsListPanel: React.FC<Props> = ({clientContacts, primaryContactId, viewerProjects, clientId, clientName}) => {
	const {formatMessage, formatDate} = useIntl();
	const history = useHistory();
	const [filter, setFilter] = useState<'CURRENT' | 'ALL'>('CURRENT');
	const [hoveredBadgeContactId, setHoveredBadgeContactId] = useState<string | null>(null);
	const [primaryTooltipVisible, setPrimaryTooltipVisible] = useState(false);
	const [editingClientContactId, setEditingClientContactId] = useState<string | null>(null);
	const [creatingContact, setCreatingContact] = useState(false);

	const handleEditContact = (contact: ClientContactNode) => {
		if (contact.isGuestUser) {
			if (contact.person?.id) {
				history.push(`/admin/team/view-user/${contact.person.id}`);
			}
		} else {
			setEditingClientContactId(contact.id);
		}
	};

	const visibleContacts = useMemo(
		() => (filter === 'CURRENT' ? clientContacts.filter(c => c.status === 'ACTIVE') : clientContacts),
		[clientContacts, filter]
	);

	const personIdToProjectsMap = useMemo(() => {
		const map = new Map<string, PersonProjectAccess[]>();
		viewerProjects.forEach(project => {
			project.projectPersons?.edges?.forEach(pp => {
				const node = pp?.node;
				if (!node?.person?.id) return;
				const existing = map.get(node.person.id) ?? [];
				existing.push({
					personProjectId: node.id,
					projectId: project.id,
					companyProjectId: project.companyProjectId,
					projectName: project.name ?? '',
					projectEndYear: project.projectEndYear,
					projectEndMonth: project.projectEndMonth,
					projectEndDay: project.projectEndDay,
				});
				map.set(node.person.id, existing);
			});
		});
		return map;
	}, [viewerProjects]);
	const formatProjectDueDate = (year: number | null, month: number | null, day: number | null): string => {
		if (!year || !month || !day) return '';
		return formatDate(new Date(year, month - 1, day), {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	};

	return (
		<PanelWrapper>
			<ToolbarWrapper>
				<TableBar title={formatMessage({id: 'contacts_panel.title'})}>
					<SegmentedControlWrapper>
						<SegmentButton $active={filter === 'CURRENT'} onClick={() => setFilter('CURRENT')}>
							{formatMessage({id: 'contacts_panel.filter_current'})}
						</SegmentButton>
						<SegmentButton $active={filter === 'ALL'} onClick={() => setFilter('ALL')}>
							{formatMessage({id: 'contacts_panel.filter_all'})}
						</SegmentButton>
					</SegmentedControlWrapper>
					{/* onPress wired up in Task 8 (create-contact modal) */}
					<Button emphasis="medium" onPress={() => setCreatingContact(true)}>
						{formatMessage({id: 'contacts_panel.create_contact'})}
					</Button>
				</TableBar>
			</ToolbarWrapper>
			<ContentArea>
				{visibleContacts.length === 0 ? (
					<EmptyStateBox>
						<EmptyStateText>{formatMessage({id: 'contacts_panel.empty_state'})}</EmptyStateText>
						<CreateContactLink onClick={() => setCreatingContact(true)}>
							{formatMessage({id: 'contacts_panel.create_contact'})}
						</CreateContactLink>
					</EmptyStateBox>
				) : (
					<ContactTable>
						{/* Header row */}
						<TableHeaderRow>
							<NameColHeader>{formatMessage({id: 'contacts_panel.col_name'})}</NameColHeader>
							<TextCell style={{flex: 1}}>{formatMessage({id: 'contacts_panel.col_email'})}</TextCell>
							<TextCell style={{flex: 1}}>{formatMessage({id: 'contacts_panel.col_phone'})}</TextCell>
							<TextCell style={{flex: 1}}>{formatMessage({id: 'contacts_panel.col_position'})}</TextCell>
							<TextCell style={{width: 80, flex: 'none' as const}}>
								{formatMessage({id: 'contacts_panel.col_status'})}
							</TextCell>
							<TextCell style={{width: 120, flex: 'none' as const}}>
								{formatMessage({id: 'contacts_panel.col_guest_user'})}
							</TextCell>
							<TextCell style={{width: 160, flex: 'none' as const}} />
						</TableHeaderRow>

						{visibleContacts.map(contact => {
							const initials = `${contact.firstName?.[0] ?? ''}${contact.lastName?.[0] ?? ''}`.toUpperCase();
							const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
							const projectAccesses = contact.person?.id
								? personIdToProjectsMap.get(contact.person.id) ?? []
								: [];
							const statusLabel =
								contact.status === 'ACTIVE'
									? 'Active'
									: contact.status === 'INACTIVE'
									? 'Inactive'
									: contact.status ?? '-';

							return (
								<TableRow key={contact.id}>
									<NameCell style={{flex: 1, minWidth: 0}}>
										<Avatar>{initials}</Avatar>
										<ContactName>{fullName}</ContactName>
									</NameCell>
									<TextCell style={{flex: 1}}>{contact.email ?? '-'}</TextCell>
									<TextCell style={{flex: 1}}>{contact.phone ?? '-'}</TextCell>
									<TextCell style={{flex: 1}}>{contact.jobTitle ?? '-'}</TextCell>
									<TextCell style={{width: 80, flex: 'none' as const}}>{statusLabel}</TextCell>
									<GuestCell style={{width: 120, flex: 'none' as const, position: 'relative'}}>
										{contact.isGuestUser ? 'Yes' : 'No'}
										{contact.isGuestUser && projectAccesses.length > 0 && (
											<ProjectBadge
												data-testid="project-count-badge"
												onClick={e => {
													e.stopPropagation();
													setHoveredBadgeContactId(prev => (prev === contact.id ? null : contact.id));
												}}
											>
												{projectAccesses.length}
												{hoveredBadgeContactId === contact.id && (
													<ProjectTooltip>
														<TooltipHeader>
															<TooltipTitle>
																{formatMessage({
																	id: 'contacts_panel.tooltip_guest_access_title',
																})}
															</TooltipTitle>
															<TooltipCloseButton
																onClick={e => {
																	e.stopPropagation();
																	setHoveredBadgeContactId(null);
																}}
																aria-label="Close"
															>
																✕
															</TooltipCloseButton>
														</TooltipHeader>
														<TooltipTable>
															<TooltipHeaderRow>
																<TooltipColId>
																	{formatMessage({id: 'contacts_panel.tooltip_col_id'})}
																</TooltipColId>
																<TooltipColTitle>
																	{formatMessage({
																		id: 'contacts_panel.tooltip_col_project_title',
																	})}
																</TooltipColTitle>
																<TooltipColDate>
																	{formatMessage({id: 'contacts_panel.tooltip_col_due_date'})}
																</TooltipColDate>
																<TooltipColAction />
															</TooltipHeaderRow>
															{projectAccesses.map((pa, i) => (
																<TooltipTableRow
																	key={pa.personProjectId}
																	$last={i === projectAccesses.length - 1}
																>
																	<TooltipDataColId>
																		<ProjectIdBadge
																			$color={
																				PROJECT_ID_COLORS[i % PROJECT_ID_COLORS.length]
																			}
																		>
																			{pa.companyProjectId != null
																				? `P${pa.companyProjectId}`
																				: '—'}
																		</ProjectIdBadge>
																	</TooltipDataColId>
																	<TooltipDataColTitle>
																		<span
																			style={{
																				overflow: 'hidden',
																				textOverflow: 'ellipsis',
																				whiteSpace: 'nowrap',
																			}}
																		>
																			{pa.projectName}
																		</span>
																	</TooltipDataColTitle>
																	<TooltipDataColDate>
																		{formatProjectDueDate(
																			pa.projectEndYear,
																			pa.projectEndMonth,
																			pa.projectEndDay
																		)}
																	</TooltipDataColDate>
																	<TooltipColAction>
																		<TooltipRemoveButton
																			onClick={e => {
																				e.stopPropagation();
																				Util.CommitMutation(
																					DeleteProjectPersonMutation,
																					{
																						ids: [pa.personProjectId],
																						projectId: pa.projectId,
																					},
																					() => setHoveredBadgeContactId(null)
																				);
																			}}
																			data-testid="remove-from-project"
																			aria-label="Remove from project"
																		>
																			<Icon icon="closeCircle" size="m" />
																		</TooltipRemoveButton>
																	</TooltipColAction>
																</TooltipTableRow>
															))}
														</TooltipTable>
													</ProjectTooltip>
												)}
											</ProjectBadge>
										)}
									</GuestCell>
									<ActionsCell>
										{contact.id === primaryContactId && (
											<PrimaryBadgeWrapper>
												<PrimaryBadge
													data-testid="primary-badge"
													onMouseEnter={() => setPrimaryTooltipVisible(true)}
													onMouseLeave={() => setPrimaryTooltipVisible(false)}
												>
													{formatMessage({id: 'contacts_panel.badge_primary'})}
												</PrimaryBadge>
												{primaryTooltipVisible && (
													<PrimaryTooltip data-testid="primary-badge-tooltip">
														<PrimaryTooltipTitle>
															{formatMessage({id: 'contacts_panel.badge_primary_tooltip_title'})}
														</PrimaryTooltipTitle>
														<PrimaryTooltipBody>
															{formatMessage({id: 'contacts_panel.badge_primary_tooltip_body'})}
														</PrimaryTooltipBody>
													</PrimaryTooltip>
												)}
											</PrimaryBadgeWrapper>
										)}
										<DropdownMenu
											trigger={<KebabButton data-testid={`kebab-menu-${contact.id}`}>···</KebabButton>}
										>
											<DropdownMenu.Item onClick={() => handleEditContact(contact)}>
												{formatMessage({id: 'contacts_panel.action_edit'})}
											</DropdownMenu.Item>
											<DropdownMenu.Item onClick={noop}>
												{formatMessage({id: 'contacts_panel.action_delete'})}
											</DropdownMenu.Item>
										</DropdownMenu>
									</ActionsCell>
								</TableRow>
							);
						})}
					</ContactTable>
				)}
			</ContentArea>
			{editingClientContactId && (
				<EditClientContactModalContainer
					clientContactId={editingClientContactId}
					onClose={() => setEditingClientContactId(null)}
				/>
			)}
			{creatingContact && (
				<ClientContactModalContainer
					mode="contact"
					entryPoint="client-page"
					clientId={clientId}
					preSelectedClientName={clientName}
					onClose={() => setCreatingContact(false)}
				/>
			)}
		</PanelWrapper>
	);
};

export default ContactsListPanel;
